// Services
import localStorageService from '../utils/services/localStorage/localStorage';
import { removeCapacitorAuthCookie, setCapacitorAuthCookie } from './capacitorAuthService';

// Utils
import { toEpochMs } from '../../common/utils/timeUtil';

// Constants
import { USER_LOCAL_STORAGE_TOKEN_KEY } from './authConstants';

const BETTER_AUTH_SESSION_USER_LOCAL_STORAGE_KEY = 'betterAuthSessionUser';

// Cached user data so we don't need to query the server on app init.
export type BetterAuthSessionUser = {
    _id: string;
    rootBoardId: string;
    // The expiry value taken from the session (epoch ms). Recorded so we can identify forced logouts
    // by checking it when we get a "missing authentication" error. This can't be done on the server
    // because the cookie's maxAge and the session's expiresIn are the same, so the cookie is deleted
    // from the browser at the same moment it expires — an expired token is never sent to the server
    // for it to return an "expired token" error.
    sessionExpiresAt?: number;
};

export const getCurrentUserToken = (): string | null => localStorageService.getItem(USER_LOCAL_STORAGE_TOKEN_KEY);

export const setCurrentUserToken = (value: string): void => {
    localStorageService.setItem(USER_LOCAL_STORAGE_TOKEN_KEY, value);

    // Update token stored on Electron main process

    window.desktopIntegration?.setMnToken?.(value);

    // Update token stored on the Native Capacitor side
    setCapacitorAuthCookie(value);
};

export const removeCurrentUserToken = (): void => {
    localStorageService.removeItem(USER_LOCAL_STORAGE_TOKEN_KEY);

    // Update token stored on Electron main process

    window.desktopIntegration?.setMnToken?.(undefined);

    // Remove token stored on the Native Capacitor side
    removeCapacitorAuthCookie();
};

const getRememberedBetterAuthSessionUserData = (user: unknown): BetterAuthSessionUser | null => {
    if (!user || typeof user !== 'object') return null;

    const sessionUser = user as Partial<BetterAuthSessionUser>;
    if (typeof sessionUser._id !== 'string' || typeof sessionUser.rootBoardId !== 'string') return null;

    const sessionExpiresAt = toEpochMs(sessionUser.sessionExpiresAt);

    return {
        _id: sessionUser._id,
        rootBoardId: sessionUser.rootBoardId,
        ...(sessionExpiresAt != null ? { sessionExpiresAt } : {}),
    };
};

export const getRememberedBetterAuthSessionUser = (): BetterAuthSessionUser | null => {
    const rawSessionUser = localStorageService.getItem(BETTER_AUTH_SESSION_USER_LOCAL_STORAGE_KEY);
    if (!rawSessionUser) return null;

    try {
        const sessionUser = JSON.parse(rawSessionUser);
        return getRememberedBetterAuthSessionUserData(sessionUser);
    } catch {
        localStorageService.removeItem(BETTER_AUTH_SESSION_USER_LOCAL_STORAGE_KEY);
        return null;
    }
};

export const getHasRememberedBetterAuthSessionUser = (): boolean => !!getRememberedBetterAuthSessionUser();

/**
 * Whether local storage holds anything suggesting this browser has a session to restore — either
 * auth system's. Resolves synchronously, so it can seed `isGuest` before any request is made.
 *
 * Shared deliberately: this used to be spelled out separately in the current user reducer, the
 * Electron initial state and AuthenticatedContainer, and they drifted apart during the Better Auth
 * migration.
 */
export const getHasLocalAuthHint = (): boolean => !!getCurrentUserToken() || getHasRememberedBetterAuthSessionUser();

export const setRememberedBetterAuthSessionUser = (
    user: unknown,
    options: { sessionExpiresAt?: number | string } = {},
): void => {
    const sessionUser = getRememberedBetterAuthSessionUserData(user);
    if (!sessionUser) return;

    // Prefer the freshly-provided expiry; otherwise keep whatever we already had so a refresh that
    // lacks it (e.g. a legacy-auth response) does not wipe a known expiry. Only carry the stored
    // expiry forward when it belongs to the same user, so a different user can't inherit it.
    const existing = getRememberedBetterAuthSessionUser();
    const preservedExpiresAt = existing?._id === sessionUser._id ? existing?.sessionExpiresAt : undefined;
    const sessionExpiresAt = toEpochMs(options.sessionExpiresAt) ?? preservedExpiresAt;

    localStorageService.setItem(
        BETTER_AUTH_SESSION_USER_LOCAL_STORAGE_KEY,
        JSON.stringify({ ...sessionUser, ...(sessionExpiresAt != null ? { sessionExpiresAt } : {}) }),
    );
};

export const removeRememberedBetterAuthSessionUser = (): void => {
    localStorageService.removeItem(BETTER_AUTH_SESSION_USER_LOCAL_STORAGE_KEY);
};
